import React, { useState } from 'react';
import {
  FiClock,
  FiSliders,
  FiCalendar,
  FiMaximize2,
  FiGlobe,
  FiGrid,
  FiCheckCircle,
} from 'react-icons/fi';
import { TbKeyboard } from 'react-icons/tb';
import { MdLightbulb } from 'react-icons/md';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Dropdown, borderedSelectStyles } from '@components/Dropdown';
import { Callout, calloutVariants } from '@components/Callout';
import { addNotification, notificationStates } from '@components/Notification';

import { SettingsFieldCard } from './SettingsFieldCard.component';
import { MonitoringOptionCard } from './MonitoringOptionCard.component';
import {
  CAPTURE_FREQUENCY_OPTIONS,
  SCREENSHOT_QUALITY_OPTIONS,
  CAPTURE_DURING_OPTIONS,
  MONITORING_OPTIONS,
  DEFAULT_MONITORING_OPTIONS,
} from '../SettingsRedesign.constants';

import styles from './ScreenshotSettingsTab.module.scss';

const MONITORING_ICON_MAP = {
  activeWindow: FiMaximize2,
  keyboardActivity: TbKeyboard,
  webActivity: FiGlobe,
  applicationUsage: FiGrid,
  idleTime: FiClock,
};

const findOption = (options, value) =>
  options.find((option) => option.value === value) || null;

export const ScreenshotSettingsTab = () => {
  const [captureFrequency, setCaptureFrequency] = useState('3');
  const [screenshotQuality, setScreenshotQuality] = useState('medium');
  const [captureDuring, setCaptureDuring] = useState('working-hours');
  const [monitoringOptions, setMonitoringOptions] = useState(
    DEFAULT_MONITORING_OPTIONS
  );

  const toggleMonitoringOption = (key) => {
    setMonitoringOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    addNotification({
      message: 'Screenshot & monitoring settings saved',
      level: notificationStates.success,
    });
  };

  return (
    <>
      <div className={styles.sectionHeading}>
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Screenshot Capture
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-600"
        >
          Control how frequently screenshots are captured on employee devices.
        </Text>
      </div>

      <div className={styles.captureRow}>
        <SettingsFieldCard
          icon={<FiClock size={16} color="var(--indigo-600)" />}
          label="Capture Frequency"
          helperText="Screenshots will be captured at this interval"
        >
          <Dropdown
            options={CAPTURE_FREQUENCY_OPTIONS}
            value={findOption(CAPTURE_FREQUENCY_OPTIONS, captureFrequency)}
            onChange={(option) => setCaptureFrequency(option.value)}
            selectStyles={borderedSelectStyles}
          />
        </SettingsFieldCard>
        <SettingsFieldCard
          icon={<FiSliders size={16} color="var(--indigo-600)" />}
          label="Screenshot Quality"
          helperText="Balance between quality and storage usage"
        >
          <Dropdown
            options={SCREENSHOT_QUALITY_OPTIONS}
            value={findOption(SCREENSHOT_QUALITY_OPTIONS, screenshotQuality)}
            onChange={(option) => setScreenshotQuality(option.value)}
            selectStyles={borderedSelectStyles}
          />
        </SettingsFieldCard>
      </div>

      <div className={styles.duringRow}>
        <SettingsFieldCard
          icon={<FiCalendar size={16} color="var(--indigo-600)" />}
          label="Capture During"
          helperText="Screenshots will be captured only during working hours"
          className={styles.duringCard}
        >
          <Dropdown
            options={CAPTURE_DURING_OPTIONS}
            value={findOption(CAPTURE_DURING_OPTIONS, captureDuring)}
            onChange={(option) => setCaptureDuring(option.value)}
            selectStyles={borderedSelectStyles}
          />
        </SettingsFieldCard>
        <Callout
          variant={calloutVariants.tip}
          icon={<MdLightbulb size={14} />}
          title="Tip"
          className={styles.tipCallout}
        >
          Lower frequency and quality can help reduce storage and bandwidth
          usage.
        </Callout>
      </div>

      <div className={styles.sectionHeading}>
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Monitoring Options
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-600"
        >
          Choose what activities to monitor on employee devices.
        </Text>
      </div>

      <div className={styles.monitoringGrid}>
        {MONITORING_OPTIONS.map((option) => {
          const Icon = MONITORING_ICON_MAP[option.key];
          return (
            <MonitoringOptionCard
              key={option.key}
              id={option.key}
              icon={<Icon size={18} color={`var(--${option.iconColor})`} />}
              iconBg={option.iconBg}
              title={option.title}
              description={option.description}
              checked={!!monitoringOptions[option.key]}
              onChange={toggleMonitoringOption}
            />
          );
        })}
      </div>

      <div className={styles.saveRow}>
        <Button
          text="Save Changes"
          variant={buttonVariants.primary}
          icon={<FiCheckCircle size={16} />}
          onClick={handleSave}
        />
      </div>
    </>
  );
};

ScreenshotSettingsTab.displayName = 'ScreenshotSettingsTab';
